import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Eye, Heart, Users, Award, Leaf, Infinity, Sparkles, TreePine, Droplets, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { PageSEO } from '../components/PageSEO';

const ABOUT_IMG = '/images/hikayemiz-hero.webp';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const AboutPage = () => {
  const [lightbox, setLightbox] = useState(null);

  const showcaseItems = [
    { src: '/images/logo-showcase/sweatshirt.webp', alt: 'KOSAD Sweatshirt', label: 'Sweatshirt' },
    { src: '/images/logo-showcase/hoodie.webp', alt: 'KOSAD Hoodie', label: 'Hoodie' },
    { src: '/images/logo-showcase/tshirt.webp', alt: 'KOSAD T-Shirt', label: 'T-Shirt' },
    { src: '/images/logo-showcase/cap2.webp', alt: 'KOSAD Şapka', label: 'Şapka' },
    { src: '/images/logo-showcase/bag3.webp', alt: 'KOSAD Bez Çanta', label: 'Bez Çanta' },
    { src: '/images/logo-showcase/relaxed.webp', alt: 'KOSAD Relaxed Hoodie', label: 'Relaxed' },
    { src: '/images/logo-showcase/mug.webp', alt: 'KOSAD Kupa', label: 'Kupa' },
    { src: '/images/logo-showcase/scarf2.webp', alt: 'KOSAD Fular', label: 'Fular' },
    { src: '/images/logo-showcase/yakakarti.webp', alt: 'KOSAD Yaka Kartı', label: 'Yaka Kartı' },
    { src: '/images/logo-showcase/fall.webp', alt: 'KOSAD Bere', label: 'Bere' },
    { src: '/images/logo-showcase/tshirt2.webp', alt: 'KOSAD T-Shirt', label: 'T-Shirt' },
    { src: '/images/logo-showcase/bag.webp', alt: 'KOSAD Çanta', label: 'Çanta' },
    { src: '/images/logo-showcase/bandana2.webp', alt: 'KOSAD Bandana', label: 'Bandana' },
    { src: '/images/logo-showcase/cargo.webp', alt: 'KOSAD Kargo', label: 'Kargo' },
    { src: '/images/logo-showcase/mug2.webp', alt: 'KOSAD Fincan', label: 'Fincan' },
    { src: '/images/logo-showcase/cap.webp', alt: 'KOSAD Şapka', label: 'Şapka' },
    { src: '/images/logo-showcase/bag2.webp', alt: 'KOSAD Çanta', label: 'Çanta' },
    { src: '/images/logo-showcase/bandana.webp', alt: 'KOSAD Bandana', label: 'Bandana' },
    { src: '/images/logo-showcase/yakakarti2.webp', alt: 'KOSAD Kimlik', label: 'Kimlik' },
    { src: '/images/logo-showcase/aksesuar.webp', alt: 'KOSAD Aksesuar', label: 'Aksesuar' }
  ];

  const values = [
    {
      icon: Heart,
      title: 'Doğaya Saygı',
      description: 'Tüm çalışmalarımızda doğanın dengesini korumayı öncelik olarak görüyoruz.'
    },
    {
      icon: Users,
      title: 'Topluluk Gücü',
      description: 'Birlikte daha güçlüyüz. Dayanışma ve işbirliği ile büyüyoruz.'
    },
    {
      icon: Award,
      title: 'Kalite ve Güven',
      description: 'Üretimden tüketime her aşamada kalite ve şeffaflık ilkesiyle hareket ediyoruz.'
    },
    {
      icon: Leaf,
      title: 'Sürdürülebilirlik',
      description: 'Gelecek nesillere yaşanabilir bir dünya bırakmak için çalışıyoruz.'
    }
  ];

  const team = [
    { name: 'Ahmet Yılmaz', role: 'Başkan', image: '/images/producers/uretici1.jpg' },
    { name: 'Fatma Demir', role: 'Başkan Yardımcısı', image: '/images/producers/uretici2.jpg' },
    { name: 'Zeynep Kaya', role: 'Genel Sekreter', image: '/images/producers/uretici3.jpg' },
    { name: 'Ayşe Öztürk', role: 'Sayman', image: '/images/producers/market-uretici.jpg' }
  ];

  return (
    <div data-testid="about-page">
      <PageSEO title="Hakkımızda" description="KOSAD'ın hikayesi, vizyonu, misyonu ve değerleri. Kocaeli'de sürdürülebilir arıcılık için çalışan derneğimizi tanıyın." path="/hakkimizda" />
      {/* Hero */}
      <section className="relative py-32 bg-[#1A3625]" data-testid="about-hero">
        <div className="container-custom">
          <motion.div 
            className="max-w-3xl"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Hakkımızda</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl text-white font-semibold mb-6">
              KOSAD'ı Tanıyın
            </h1>
            <p className="text-base text-white/80">
              Kocaeli Sürdürülebilir Arıcılık ve Dayanışma Derneği olarak, 
              arıların ve ekosistemin korunması için çalışıyoruz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section id="hikaye" className="section-padding" data-testid="story-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="label-uppercase text-[#D4AF37] mb-4 block">Hikayemiz</span>
              <h2 className="text-3xl md:text-4xl text-[#1A3625] mb-6">
                Bir Tutku İle Başladı
              </h2>
              <div className="space-y-4 text-[#5C5C5C]">
                <p>
                  KOSAD, 2020 yılında Kocaeli'de bir grup arıcının bir araya gelmesiyle kuruldu. 
                  Ortak amacımız, sürdürülebilir arıcılık uygulamalarını yaygınlaştırmak ve 
                  arı popülasyonlarının korunmasına katkı sağlamaktı.
                </p>
                <p>
                  Yıllar içinde, eğitim programlarımız, kadın girişimci desteklerimiz ve 
                  ekosistem koruma projelerimizle büyüdük. Bugün, 500'den fazla üye arıcıyla 
                  birlikte Kocaeli ve çevresinde önemli bir etki yaratıyoruz.
                </p>
                <p>
                  KOSAD Ekosistem Marketi ile üreticilerimizin ürünlerini tüketicilere 
                  doğrudan ulaştırarak, hem ekonomik sürdürülebilirliği hem de gıda 
                  güvenliğini destekliyoruz.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={ABOUT_IMG}
                alt="KOSAD Topluluğu"
                className="rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Logo Story - Premium Section */}
      <section id="logo-hikaye" className="section-padding relative overflow-hidden" data-testid="logo-story-section">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#F9F8F6] to-white" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-72 h-72 bg-[#1A3625]/5 rounded-full blur-3xl" />
        
        <div className="container-custom relative">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Kimliğimiz</span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl text-[#1A3625] mb-4">
              Logo Hikayemiz
            </h2>
            <p className="text-base text-[#5C5C5C] max-w-2xl mx-auto">
              Bir sembolden fazlası: KOSAD logosunun ardındaki anlam ve tasarım felsefesi
            </p>
          </motion.div>

          {/* Logo Display */}
          <motion.div 
            className="flex justify-center mb-20"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-[#D4AF37]/10 rounded-full blur-3xl scale-150" />
              <img 
                src="/images/kosad-logo.png" 
                alt="KOSAD Logo" 
                className="relative w-[500px] md:w-[650px] lg:w-[750px] h-auto drop-shadow-xl"
                data-testid="logo-story-image"
              />
            </div>
          </motion.div>

          {/* Main Story Text */}
          <motion.div 
            className="max-w-3xl mx-auto text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-base md:text-lg text-[#5C5C5C] leading-relaxed italic">
              "Bu logo, Kocaeli Sürdürülebilir Arıcılık ve Dayanışma Derneği'nin vizyonunu 
              ve değerlerini tek bir sembolde birleştirmek amacıyla tasarlanmıştır."
            </p>
          </motion.div>

          {/* Symbol Meanings - Staggered Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            {/* Bee Symbol */}
            <motion.div
              className="relative group"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="card-glass p-8 h-full border border-[#D4AF37]/10 hover:border-[#D4AF37]/30 transition-all duration-500">
                <div className="w-14 h-14 bg-gradient-to-br from-[#D4AF37] to-[#c9a432] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#1A3625] mb-3">Arı Figürü</h3>
                <p className="text-base text-[#5C5C5C] leading-relaxed">
                  Tasarımın merkezinde yer alan arı figürü, doğanın en çalışkan ve en kritik 
                  canlılarından birini temsil eder. Arılar sadece bal üretmez; aynı zamanda 
                  ekosistemin devamlılığı için hayati öneme sahip olan tozlaşma döngüsünün 
                  taşıyıcılarıdır. Üretimin, emeğin ve doğayla uyumlu yaşamın sembolüdür.
                </p>
              </div>
            </motion.div>

            {/* Infinity Symbol */}
            <motion.div
              className="relative group"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="card-glass p-8 h-full border border-[#1A3625]/10 hover:border-[#1A3625]/30 transition-all duration-500">
                <div className="w-14 h-14 bg-gradient-to-br from-[#1A3625] to-[#2d5a3f] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Infinity className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#1A3625] mb-3">Sonsuzluk Formu</h3>
                <p className="text-base text-[#5C5C5C] leading-relaxed">
                  Arının iki yanında yer alan form, bilinçli olarak sonsuzluk sembolünü 
                  andıracak şekilde tasarlanmıştır. Bu form; doğanın döngüsünün kesintisiz 
                  devamını, birlikte üretmenin gücünü ve arıcılığın gelecek nesillere 
                  aktarılmasını simgeler.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Color Meanings */}
          <motion.div
            className="relative mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-[#1A3625] rounded-3xl p-10 md:p-14 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#2d5a3f]/30 rounded-full blur-3xl" />
              
              <div className="relative">
                <h3 className="text-xl md:text-2xl font-semibold text-white mb-8 text-center">
                  Renklerin Dili
                </h3>
                
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Green */}
                  <div className="flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#2d5a3f] to-[#4a8c5e] flex items-center justify-center shadow-lg">
                      <TreePine className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-[#D4AF37] font-medium mb-2">Yeşil Tonları</h4>
                      <p className="text-white/70 text-base leading-relaxed">
                        Doğayı, ormanları ve ekosistemi temsil eder. Kanatların yeşil geçişleri, 
                        yaşamın sürekliliğini ve doğanın koruyucu gücünü simgeler.
                      </p>
                    </div>
                  </div>

                  {/* Gold */}
                  <div className="flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#c9a432] flex items-center justify-center shadow-lg">
                      <Droplets className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-[#D4AF37] font-medium mb-2">Altın Sarısı Tonları</h4>
                      <p className="text-white/70 text-base leading-relaxed">
                        Balın bereketini, üretimi ve emeğin değerini simgeler. Bu iki rengin 
                        akışkan birleşimi, doğa ile üretim arasındaki dengeyi ve uyumu ifade eder.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Three Pillars */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { 
                label: 'Sürdürülebilirlik', 
                desc: 'Doğanın döngüsünün kesintisiz devam etmesi',
                icon: Leaf
              },
              { 
                label: 'Dayanışma', 
                desc: 'Birlikte üretmenin gücü',
                icon: Users
              },
              { 
                label: 'Sürekli Gelişim', 
                desc: 'Arıcılığın gelecek nesillere aktarılması',
                icon: Award
              }
            ].map((pillar, index) => (
              <motion.div
                key={index}
                className="text-center card-premium group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                data-testid={`logo-pillar-${index}`}
              >
                <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
                  <pillar.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h4 className="text-lg font-semibold text-[#1A3625] mb-2">{pillar.label}</h4>
                <p className="text-base text-[#5C5C5C]">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Logo Showcase Gallery */}
          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl md:text-2xl font-semibold text-[#1A3625] mb-3 text-center">
              Logomuz Hayatta
            </h3>
            <p className="text-base text-[#5C5C5C] text-center mb-10 max-w-lg mx-auto">
              KOSAD kimliğinin farklı uygulamalardaki yansıması — tıklayarak büyük görüntüleyin
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="logo-showcase-gallery">
              {showcaseItems.map((item, index) => (
                <motion.div
                  key={index}
                  className="group relative rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-500 aspect-[4/5] cursor-pointer"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: Math.min(index * 0.05, 0.5) }}
                  whileHover={{ y: -4 }}
                  onClick={() => setLightbox(index)}
                  data-testid={`logo-showcase-${index}`}
                >
                  <img 
                    src={item.src} 
                    alt={item.alt} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A3625]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                    <span className="text-white text-sm font-medium">{item.label}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Lightbox Modal */}
          <AnimatePresence>
            {lightbox !== null && (
              <motion.div
                className="fixed inset-0 z-[100] flex items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                data-testid="lightbox-modal"
              >
                <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={() => setLightbox(null)} />
                
                <motion.div
                  className="relative max-w-4xl max-h-[85vh] mx-4"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                >
                  <img 
                    src={showcaseItems[lightbox].src} 
                    alt={showcaseItems[lightbox].alt}
                    className="max-h-[85vh] w-auto rounded-2xl shadow-2xl object-contain"
                    data-testid="lightbox-image"
                  />
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full">
                    <span className="text-white text-base font-medium">{showcaseItems[lightbox].label}</span>
                    <span className="text-white/50 text-sm ml-2">{lightbox + 1} / {showcaseItems.length}</span>
                  </div>
                </motion.div>

                <button 
                  onClick={() => setLightbox(null)} 
                  className="absolute top-6 right-6 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                  data-testid="lightbox-close"
                >
                  <X className="w-6 h-6" />
                </button>
                <button 
                  onClick={() => setLightbox((lightbox - 1 + showcaseItems.length) % showcaseItems.length)} 
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button 
                  onClick={() => setLightbox((lightbox + 1) % showcaseItems.length)} 
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                  data-testid="lightbox-next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Closing Statement */}
          <motion.div 
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto mb-8" />
            <p className="text-base text-[#1A3625] font-medium leading-relaxed">
              KOSAD logosu, arıların doğadaki rolünden ilham alan bir gelecek vizyonunu simgeler. 
              Doğayla uyumlu üretimi, sürdürülebilir arıcılığı, yerel dayanışmayı ve 
              ekosistemin korunmasını temsil eder.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section id="vizyon" className="section-padding bg-white" data-testid="vision-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              className="bg-[#1A3625] text-white rounded-3xl p-8 relative overflow-hidden"
              style={{ boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)' }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-[#D4AF37] rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-[#1A3625]" />
              </div>
              <h3 className="text-2xl font-medium mb-4">Vizyonumuz</h3>
              <p className="text-white/80 text-base leading-relaxed">
                Sürdürülebilir arıcılık uygulamalarının yaygınlaştığı, arı popülasyonlarının 
                korunduğu, üreticilerin ekonomik olarak güçlendiği ve toplumun doğa ile 
                uyumlu yaşadığı bir dünya.
              </p>
            </motion.div>
            
            <motion.div
              className="card-premium"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-16 h-16 bg-[#1A3625] rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-medium text-[#1A3625] mb-4">Misyonumuz</h3>
              <p className="text-[#5C5C5C] text-base">
                Arıcılık sektöründe sürdürülebilir uygulamaları yaygınlaştırmak, arıcıları 
                eğitmek ve desteklemek, kadın üreticilerin ekonomik bağımsızlığını sağlamak 
                ve ekosistem farkındalığını artırmak.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section id="degerler" className="section-padding" data-testid="values-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Değerlerimiz</span>
            <h2 className="text-3xl md:text-4xl text-[#1A3625]">
              Bizi Yönlendiren İlkeler
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="card-premium text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 bg-[#1A3625]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-[#1A3625]" />
                </div>
                <h3 className="text-lg font-medium text-[#1A3625] mb-2">{value.title}</h3>
                <p className="text-[#5C5C5C]">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="ekip" className="section-padding bg-white" data-testid="team-section">
        <div className="container-custom">
          <div className="text-center">
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Ekibimiz</span>
            <h2 className="text-3xl md:text-4xl text-[#1A3625] mb-6">
              Yönetim Kurulu
            </h2>
            <div className="max-w-md mx-auto bg-[#F9F8F6] rounded-2xl p-8 border border-[#E5DAC8]/50">
              <p className="text-lg text-[#5C5C5C]">Güncellenmektedir</p>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Goals */}
      <section className="section-padding bg-[#1A3625]" data-testid="goals-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Stratejik Hedefler</span>
            <h2 className="text-3xl md:text-4xl text-white">
              2026 Yol Haritamız
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { number: '01', title: '1000 Üye', description: 'Üye sayımızı 1000\'e çıkarmak ve daha geniş bir topluluk oluşturmak.' },
              { number: '02', title: '50 Kadın Girişimci', description: 'Kadın arıcı girişimci sayısını ikiye katlamak.' },
              { number: '03', title: '10 Yeni Proje', description: 'Ekosistem koruma ve eğitim alanında 10 yeni proje başlatmak.' }
            ].map((goal, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <span className="text-7xl font-bold text-white/10 absolute -top-4 left-0">{goal.number}</span>
                <div className="relative pt-10">
                  <h3 className="text-xl font-medium text-[#D4AF37] mb-2">{goal.title}</h3>
                  <p className="text-white/70 text-base">{goal.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
